import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import { styled, SwitchProps, Theme } from "@mui/material";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import { JSX } from "react";
import { PermissionToggle } from "permissions/_views/tables/_components/permission-toggle";
import {
  getControlMeta,
  classNameFromResource,
  getCardStyle,
  getResourceBaseColor,
} from "permissions/_views/tables/_utilities/utils";
import {
  PermissionCardHeader,
  PermissionTitleContainer,
  SinglePermissionActions,
  SmallSelect,
} from "permissions/_views/tables/permissions-table-styles";
import type { Resource } from "permissions/resources/permission-resource";
import {
  UniversalCellConfig,
  CardType,
  CategoryLabel,
  ControlMeta,
  MetaControlType,
} from "permissions/_views/types/permission-types";

const PackedCard = styled(Card)<{ cols: number; rows: number }>(({ cols, rows }) => ({
  alignItems: "center",
  gridColumn: `span ${cols}`,
  gridRow: `span ${rows}`,
  display: "flex",
  flexDirection: "column",
  gridTemplateRows: "min-content minmax(0, 1fr)",
  height: "100%",
  overflowY: "auto",
  position: "relative",
  minWidth: "150px",
}));

const DisabledText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: 500,
  textAlign: "center",
}));

const Text = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  textAlign: "center",
}));

export type SingleTemplatePermissionCardProps = {
  resource: Resource | undefined;
  config: Extract<UniversalCellConfig, { cardType: CardType.Single }>;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  getCardSubheader(resource: Resource): string | undefined;
  canEdit?: boolean;
  onToggle(resource: Resource): void;
  onApplyMeta(resource: Resource, meta: ControlMeta, newValue: string): void;
};

export default function SingleTemplatePermissionCard(
  props: Readonly<SingleTemplatePermissionCardProps>
): JSX.Element | null {
  const { resource, config, theme, activeCategory, getCardSubheader, canEdit, onToggle, onApplyMeta } = props;

  if (!resource) return null;

  const accessLevelMeta: ControlMeta | null = getControlMeta(resource, MetaControlType.AccessLevel);
  const secondMeta: ControlMeta | null =
    getControlMeta(resource, MetaControlType.AccessType) ?? getControlMeta(resource, MetaControlType.Scope);

  const cardTitle = classNameFromResource(config.resource);
  const cardSubHeader = getCardSubheader(resource);

  const { bgColor, borderColor, textColor, display, selectSx } = getCardStyle(
    theme,
    resource,
    resource.isDisabled,
    activeCategory
  );

  return (
    <PackedCard
      cols={config.colSpan ?? 2}
      rows={config.rowSpan ?? 2}
      sx={{ backgroundColor: bgColor, borderColor, color: textColor, display }}
      variant="outlined"
    >
      <PermissionCardHeader
        sx={{ p: 1 }}
        avatar={
          canEdit ? (
            <PermissionToggle
              checked={!resource.isDisabled}
              disabled={!canEdit}
              onChange={() => onToggle(resource)}
              color={getResourceBaseColor(resource) as SwitchProps["color"]}
            />
          ) : null
        }
        title={<PermissionTitleContainer grouped={false}>{cardTitle}</PermissionTitleContainer>}
        subheader={cardSubHeader}
        slotProps={{
          subheader: {
            sx: {
              title: cardSubHeader,
              maxWidth: "150px",
              overflow: "hidden",
              textAlign: "left",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
            },
          },
        }}
      />
      <SinglePermissionActions>
        <Stack direction="column" sx={{ gap: canEdit ? 1 : 0 }}>
          {!canEdit && resource.isDisabled && <DisabledText>Disabled</DisabledText>}
          {!canEdit && !resource.isDisabled && (
            <>
              <Text>{accessLevelMeta?.value}</Text>
              <Text>{secondMeta?.value}</Text>
            </>
          )}
          {canEdit && (
            <>
              {accessLevelMeta && (
                <SmallSelect
                  size="small"
                  disabled={!canEdit || resource.isDisabled}
                  sx={selectSx}
                  value={accessLevelMeta.value}
                  onChange={(e) => onApplyMeta(resource, accessLevelMeta, String(e.target.value))}
                >
                  {accessLevelMeta.options.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </SmallSelect>
              )}
              {secondMeta && (
                <SmallSelect
                  size="small"
                  disabled={!canEdit || resource.isDisabled}
                  sx={selectSx}
                  value={secondMeta.value}
                  onChange={(e) => onApplyMeta(resource, secondMeta, String(e.target.value))}
                >
                  {secondMeta.options.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </SmallSelect>
              )}
            </>
          )}
        </Stack>
      </SinglePermissionActions>
    </PackedCard>
  );
}
